// Authentication service - single source of truth for token management
// This avoids circular imports by centralizing all token operations

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Token storage keys
const ACCESS_TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

// Token management functions
export const setTokens = (accessToken: string, refreshToken: string): void => {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
};

export const getAccessToken = (): string | null => {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
};

export const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

export const clearTokens = (): void => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  // Clear any other app-specific data
  localStorage.removeItem('current_user');
  localStorage.removeItem('selectedStoryId');
  localStorage.removeItem('availableStories');
};

// Check if user is authenticated (has valid access token)
export const isAuthenticated = (): boolean => {
  const token = getAccessToken();
  return token !== null && token.length > 0;
};

// Refresh access token using refresh token
export const refreshAccessToken = async (): Promise<boolean> => {
  const refreshToken = getRefreshToken();
  
  if (!refreshToken) {
    console.log('AuthService: No refresh token available');
    return false;
  }

  try {
    console.log('AuthService: Attempting token refresh...');
    
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
      credentials: 'include',
    });

    if (response.ok) {
      const tokenData = await response.json();
      const newAccessToken = tokenData.access_token;
      const newRefreshToken = tokenData.refresh_token;

      // Update tokens with new values (token rotation)
      setTokens(newAccessToken, newRefreshToken);
      
      console.log('AuthService: Token refresh successful');
      return true;
    } else {
      console.log('AuthService: Token refresh failed with status:', response.status);
      // Refresh token is invalid, clear all tokens
      clearTokens();
      return false;
    }
  } catch (error) {
    console.error('AuthService: Token refresh error:', error);
    // Network error or other issue, clear tokens
    clearTokens();
    return false;
  }
};

// Validate current access token by calling /auth/me
export const validateAccessToken = async (): Promise<boolean> => {
  const accessToken = getAccessToken();
  
  if (!accessToken) {
    return false;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
      credentials: 'include',
    });

    if (response.ok) {
      return true;
    } else if (response.status === 401) {
      // Access token expired, try to refresh
      return await refreshAccessToken();
    } else {
      // Other error
      return false;
    }
  } catch (error) {
    console.error('AuthService: Token validation error:', error);
    return false;
  }
};
